// Detailed service catalog with pricing 27.5% below the Northwest Arkansas market average
export const DISCOUNT_RATE = 0.275;
export const DIGITAL_CARD_URL = 'https://card.iwurks360.com';

const roundTo5 = (n) => Math.round(n / 5) * 5;
export const ourPrice = (market) => roundTo5(market * (1 - DISCOUNT_RATE));

const tier = (name, market, features) => ({ name, market, price: ourPrice(market), features });

export const detailedServices = [
  {
    id: 1,
    slug: 'seo-optimization',
    title: 'SEO Optimization',
    icon: 'Search',
    billing: 'per month',
    tagline: 'Rank higher. Get found. Grow organically.',
    description:
      'Our SEO program blends technical audits, on-page optimization, authority-building content and local search dominance so your business shows up when Northwest Arkansas customers are searching.',
    deliverables: [
      'Full technical & on-page SEO audit',
      'Keyword research and competitor gap analysis',
      'Google Business Profile & local citation management',
      'Monthly link-building and content recommendations',
      'Transparent ranking & traffic reports',
    ],
    tiers: [
      tier('Starter', 1000, ['Up to 10 target keywords', 'Local SEO setup', 'Monthly report']),
      tier('Growth', 895, ['Up to 40 keywords', '2 optimized articles / mo', 'Link building', 'Bi-weekly calls']),
      tier('Enterprise', 5000, ['Unlimited keywords', 'Multi-location SEO', '6 articles / mo', 'Dedicated strategist']),
    ],
    faqs: [
      { q: 'How long until I see SEO results?', a: 'Most clients see measurable ranking and traffic gains within 90 days, with compounding growth after 6 months.' },
      { q: 'Do you guarantee #1 rankings?', a: 'No ethical agency can. We guarantee transparent work, monthly reporting and strategies aligned with Google\'s guidelines.' },
      { q: 'Is there a long-term contract?', a: 'Plans are month-to-month after an initial 3-month onboarding period.' },
    ],
  },
  {
    id: 2,
    slug: 'social-media-marketing',
    title: 'Social Media Marketing',
    icon: 'Share2',
    billing: 'per month',
    tagline: 'Turn followers into loyal customers.',
    description:
      'We plan, create and publish scroll-stopping content across Facebook, Instagram, LinkedIn and TikTok, then manage your community and run paid boosts that convert.',
    deliverables: [
      'Platform strategy & content calendar',
      'Custom graphics, reels and copywriting',
      'Community management & reply handling',
      'Paid social campaign management',
      'Monthly engagement & growth analytics',
    ],
    tiers: [
      tier('Starter', 800, ['2 platforms', '12 posts / mo', 'Monthly report']),
      tier('Growth', 1800, ['4 platforms', '20 posts + 4 reels / mo', 'Community mgmt', 'Paid boosts']),
      tier('Enterprise', 3500, ['All platforms', 'Daily posting', 'Influencer outreach', 'Dedicated manager']),
    ],
    faqs: [
      { q: 'Which platforms do you manage?', a: 'Facebook, Instagram, LinkedIn, TikTok, X and YouTube, chosen based on where your customers actually are.' },
      { q: 'Do you create the content or do we?', a: 'We handle strategy, design, copy and scheduling. You approve everything in a shared calendar.' },
      { q: 'Is ad spend included?', a: 'Management is included; paid boost budgets are billed directly to your ad account.' },
    ],
  },
  {
    id: 3,
    slug: 'ppc-advertising',
    title: 'PPC Advertising',
    icon: 'MousePointerClick',
    billing: 'per month (mgmt fee, ad spend billed separately)',
    tagline: 'Every click aimed at revenue.',
    description:
      'Google, Bing, Meta and YouTube campaigns built around intent, tight audience targeting and relentless A/B testing to drive down cost-per-lead.',
    deliverables: [
      'Campaign architecture & keyword strategy',
      'Ad copy, creative and landing-page guidance',
      'Conversion tracking & call tracking setup',
      'Weekly bid and budget optimization',
      'Live ROI dashboard',
    ],
    tiers: [
      tier('Starter', 1000, ['Ad spend up to $3k / mo', '1 platform', 'Monthly optimization']),
      tier('Growth', 2000, ['Ad spend up to $10k / mo', '2 platforms', 'Weekly optimization', 'Landing page CRO']),
      tier('Enterprise', 4000, ['Ad spend $10k+ / mo', 'Omni-channel', 'Daily optimization', 'Dedicated PPC lead']),
    ],
    faqs: [
      { q: 'How is the management fee calculated?', a: 'A flat monthly fee based on your ad-spend bracket, so you always know the cost up front.' },
      { q: 'Who owns the ad accounts?', a: 'You do. We work inside your Google, Meta or Microsoft accounts so you keep all data and history.' },
      { q: 'How fast can campaigns launch?', a: 'Typically within 7-10 business days after onboarding and tracking setup.' },
    ],
  },
  {
    id: 4,
    slug: 'content-marketing',
    title: 'Content Marketing',
    icon: 'FileText',
    billing: 'per month',
    tagline: 'Content that educates, ranks and converts.',
    description:
      'Blog articles, guides, email newsletters and lead magnets researched and written to position you as the authority in your industry.',
    deliverables: [
      'Editorial strategy and topic clusters',
      'SEO-optimized long-form articles',
      'Email newsletter design & copy',
      'Lead magnets (eBooks, checklists)',
      'Performance tracking & content refreshes',
    ],
    tiers: [
      tier('Starter', 1200, ['2 articles / mo', '1 newsletter / mo', 'Topic research']),
      tier('Growth', 2500, ['4 articles / mo', '2 newsletters / mo', 'Quarterly lead magnet']),
      tier('Enterprise', 5000, ['8+ articles / mo', 'Weekly newsletters', 'Video scripts', 'Content strategist']),
    ],
    faqs: [
      { q: 'Who writes the content?', a: 'Experienced US-based writers with industry research, edited by our strategists and optimized for SEO.' },
      { q: 'Can you match our brand voice?', a: 'Yes. We build a voice and style guide during onboarding and refine it from your feedback.' },
      { q: 'Do you publish the content for us?', a: 'Yes, we can publish directly to your CMS and distribute via email and social channels.' },
    ],
  },
  {
    id: 5,
    slug: 'brand-strategy-design',
    title: 'Brand Strategy & Design',
    icon: 'Palette',
    billing: 'one-time project',
    tagline: 'A brand people remember and trust.',
    description:
      'From positioning and messaging to logo, color and typography systems, we craft a cohesive identity that stands out in a crowded market.',
    deliverables: [
      'Brand discovery workshop',
      'Positioning, voice & messaging framework',
      'Logo suite and visual identity system',
      'Brand guidelines document',
      'Stationery, social & marketing templates',
    ],
    tiers: [
      tier('Starter', 2500, ['Logo + 2 concepts', 'Color & type system', 'Mini style guide']),
      tier('Growth', 6000, ['Full identity system', 'Messaging framework', 'Brand guidelines', 'Marketing templates']),
      tier('Enterprise', 12000, ['Brand workshop', 'Complete rebrand', 'Sub-brand architecture', 'Launch campaign']),
    ],
    faqs: [
      { q: 'How long does a brand project take?', a: 'Starter packages take 2-3 weeks; full rebrands typically run 6-10 weeks.' },
      { q: 'How many revisions do we get?', a: 'Each phase includes two structured revision rounds, with additional rounds available.' },
      { q: 'Do we own the final files?', a: 'Absolutely. You receive full ownership and all source files on completion.' },
    ],
  },
  {
    id: 6,
    slug: 'digital-strategy',
    title: 'Digital Strategy',
    icon: 'TrendingUp',
    billing: 'one-time project',
    tagline: 'A roadmap from where you are to where you want to be.',
    description:
      'We audit your current digital footprint, benchmark competitors and deliver a prioritized 12-month growth plan with clear KPIs and budgets.',
    deliverables: [
      'Digital maturity & funnel audit',
      'Competitor and market analysis',
      'Channel mix & budget allocation plan',
      '12-month roadmap with KPIs',
      'Executive presentation & Q&A',
    ],
    tiers: [
      tier('Starter', 1500, ['Digital audit', 'Quick-win report', '1 strategy session']),
      tier('Growth', 3500, ['Full audit', 'Competitor analysis', '12-month roadmap', '3 sessions']),
      tier('Enterprise', 7500, ['Multi-department strategy', 'Martech stack review', 'Quarterly reviews for 1 year']),
    ],
    faqs: [
      { q: 'What do we receive at the end?', a: 'A written roadmap with prioritized initiatives, budgets, KPIs and an executive presentation.' },
      { q: 'Can you also execute the plan?', a: 'Yes. Many clients move into ongoing service packages, with the strategy fee credited toward execution.' },
      { q: 'Do you work with our in-house team?', a: 'Yes, we frequently coach and collaborate with internal marketing teams.' },
    ],
  },
  {
    id: 7,
    slug: 'web-design-development',
    title: 'Web Design & Development',
    icon: 'Globe',
    billing: 'one-time project',
    tagline: 'Fast, beautiful websites built to convert.',
    description:
      'Custom responsive websites and web applications with conversion-focused UX, blazing performance and SEO baked in from day one.',
    deliverables: [
      'UX wireframes & custom UI design',
      'Mobile-first responsive development',
      'CMS setup and training',
      'Speed, security & SEO optimization',
      '30 days of post-launch support',
    ],
    tiers: [
      tier('Starter', 3500, ['Up to 7 pages', 'Template-based design', 'Contact forms', 'Basic SEO']),
      tier('Growth', 8000, ['Up to 15 pages', 'Custom design', 'CMS + blog', 'Integrations']),
      tier('Enterprise', 18000, ['Unlimited pages', 'E-commerce or web app', 'Custom features', 'Ongoing support']),
    ],
    faqs: [
      { q: 'What platforms do you build on?', a: 'WordPress, Webflow, Shopify and fully custom React applications, chosen for your needs.' },
      { q: 'Will the site be mobile-friendly and fast?', a: 'Every site is mobile-first, performance-optimized and tested across devices before launch.' },
      { q: 'Do you provide hosting and maintenance?', a: 'Yes, optional hosting and care plans are available after the 30-day support period.' },
    ],
  },
  {
    id: 8,
    slug: 'video-production',
    title: 'Video Production',
    icon: 'Video',
    billing: 'one-time project',
    tagline: 'Stories that move people to act.',
    description:
      'Brand films, product explainers, testimonials and short-form social video shot, edited and optimized for every platform.',
    deliverables: [
      'Concept, script and storyboard',
      'On-location or studio filming',
      'Professional editing, motion graphics & sound',
      'Platform-specific cut-downs',
      'Captions and thumbnail design',
    ],
    tiers: [
      tier('Starter', 1500, ['1 video up to 60s', 'Half-day shoot', '2 social cut-downs']),
      tier('Growth', 4000, ['2-3 videos', 'Full-day shoot', 'Motion graphics', '6 cut-downs']),
      tier('Enterprise', 9000, ['Brand film + series', 'Multi-day production', 'Drone footage', 'Monthly content']),
    ],
    faqs: [
      { q: 'Do you film on location?', a: 'Yes, anywhere in Northwest Arkansas is included; travel beyond is quoted separately.' },
      { q: 'How long is turnaround?', a: 'First cuts are delivered within 10 business days of the shoot for most projects.' },
      { q: 'Can you repurpose video for social?', a: 'Yes, every package includes platform-specific vertical and square cut-downs.' },
    ],
  },
  {
    id: 9,
    slug: 'analytics-reporting',
    title: 'Analytics & Reporting',
    icon: 'BarChart3',
    billing: 'per month',
    tagline: 'Know exactly what is working and why.',
    description:
      'GA4, Tag Manager and CRM integrations with custom dashboards that turn raw data into decisions you can act on.',
    deliverables: [
      'GA4 & Tag Manager implementation',
      'Conversion and event tracking',
      'Custom Looker Studio dashboards',
      'Monthly insight reports',
      'Attribution and funnel analysis',
    ],
    tiers: [
      tier('Starter', 600, ['GA4 setup', '1 dashboard', 'Monthly summary']),
      tier('Growth', 1500, ['Full tracking plan', '3 dashboards', 'CRM integration', 'Monthly call']),
      tier('Enterprise', 3000, ['Multi-property analytics', 'Custom attribution', 'Weekly insights', 'Data analyst']),
    ],
    faqs: [
      { q: 'Which tools do you use?', a: 'Google Analytics 4, Tag Manager, Search Console, Looker Studio and your CRM or ad platforms.' },
      { q: 'Will we understand the reports?', a: 'Reports are written in plain English with clear recommendations, not just charts.' },
      { q: 'Can you fix our broken tracking?', a: 'Yes, tracking audits and repairs are part of every Growth and Enterprise plan.' },
    ],
  },
  {
    id: 10,
    slug: 'ai-business-solutions',
    title: 'AI Business Solutions',
    icon: 'Brain',
    billing: 'one-time setup (hosting billed separately)',
    tagline: 'Automate the busywork. Scale the results.',
    description:
      'AI chatbots, phone receptionists, lead qualification and workflow automations tailored to your business, like our own UnGaro assistant.',
    deliverables: [
      'Process discovery & AI opportunity map',
      'Custom AI chatbot or voice receptionist',
      'CRM & email automation workflows',
      'Knowledge base training on your content',
      'Ongoing tuning and performance reports',
    ],
    tiers: [
      tier('Starter', 2000, ['Website AI chatbot', 'FAQ training', 'Lead capture']),
      tier('Growth', 5000, ['Chatbot + AI phone receptionist', 'CRM integration', 'Multilingual']),
      tier('Enterprise', 12000, ['Custom AI workflows', 'Multi-channel agents', 'Data pipelines', 'Dedicated AI engineer']),
    ],
    faqs: [
      { q: 'Is the AI trained on our business?', a: 'Yes, agents are trained on your services, FAQs and policies and tested before going live.' },
      { q: 'Can it speak other languages?', a: 'Growth and Enterprise plans include multilingual chat and voice support.' },
      { q: 'What are the ongoing costs?', a: 'Setup is one-time; hosting and usage are billed monthly at cost plus a small support fee.' },
    ],
  },
  {
    id: 11,
    slug: 'brand-reputation-management',
    title: 'Brand Reputation Management',
    icon: 'Shield',
    billing: 'per month',
    tagline: 'Protect the reputation you worked hard to build.',
    description:
      'Proactive review generation, 24/7 mention monitoring and strategic response management that turns feedback into trust.',
    deliverables: [
      'Review monitoring across 50+ sites',
      'Automated review request campaigns',
      'Professional response drafting',
      'Negative content suppression strategy',
      'Monthly sentiment reports',
    ],
    tiers: [
      tier('Starter', 750, ['Review monitoring', 'Response templates', 'Monthly report']),
      tier('Growth', 1800, ['Review generation campaigns', 'Managed responses', 'Sentiment tracking']),
      tier('Enterprise', 4000, ['Crisis management', 'Suppression strategy', 'PR support', 'Dedicated manager']),
    ],
    faqs: [
      { q: 'Can you remove negative reviews?', a: 'We cannot delete legitimate reviews, but we respond professionally and bury negatives with positive, authentic ones.' },
      { q: 'How do you generate new reviews?', a: 'Automated SMS and email campaigns invite happy customers to review you on Google and industry sites.' },
      { q: 'How fast do you respond to reviews?', a: 'Within one business day on all plans; same-day on Enterprise.' },
    ],
  },
];

export const digitalCardProduct = {
  title: 'iWurks360 Digital Business Card',
  tagline: 'Your entire brand in one tap.',
  description:
    'Share your contact details, social links, portfolio and booking page instantly with a smart digital business card. Works via QR code, NFC tap or a simple link, no app required.',
  features: [
    'Instant share via QR, NFC or link',
    'Unlimited updates, never reprint again',
    'Built-in lead capture & analytics',
    'Fully branded to match your identity',
  ],
  url: DIGITAL_CARD_URL,
};
